/**
 * Bridge onboarding page — the post-magic-link flow.
 *
 * Stages:
 *   1. DOB challenge (3 attempts, then burns the session)
 *   2. VERIFY — if the Mac pushed a profile for this patient, show a
 *      one-page summary ("here's what we have on file — confirm or update")
 *   3. OnboardingWizard in bridge mode — only shown if the patient says
 *      "needs update" OR if no profile was preloaded
 *   4. FEE — patient role only, and only when the bridge answers the payment
 *      status call with a `pending` row (FeeStage); 404 (no row) and 503
 *      (payments not configured) skip it, so a form without a payment row
 *      never shows a fee screen. Any other failure of that call HOLDS here
 *      with a retry (ruling 2026-10-14: the fee step is a gate now, and the
 *      bridge would refuse the form anyway); a 401 says the session expired
 *      instead, since no retry can pass it. The check runs on every entry.
 *   5. Patient-fill form fields (side-panel UI)
 *   6. Submit: seal the bundle with the bridge's pubkey, PUT to bridge
 *   7. Success page
 *
 * No free path (ruling 2026-10-14, case imp_a6b6fd85). Two more endings:
 *   DECLINED — the patient chose "I have decided not to have this form filled
 *      out" (G20, 2026-10-15; or the row
 *      already reads `declined_old_way`: checked once right after the date of
 *      birth, or on arrival when the link asks for none, and again at FEE): a
 *      closing page, never the questionnaire, the tracker or the prepared
 *      PDF.
 *   CLOSED — any other signer (the plan member) of a request the patient
 *      closed: "This request was closed. Nothing more is needed from you."
 *      Never why.
 * FEE-ONLY (G23(f), Michael approved 2026-10-16) — the PATIENT's link on a
 *    form with nothing for them to fill, which only the plan holder signs
 *    (`session.feeOnly`, from the bridge's redeem). DOB → FEE → FEE_DONE:
 *    the fee notice, card entry and the decline (FeeStage, unchanged; pay
 *    mode on a row whose card was declined), and nothing else. No details to
 *    confirm, no profile fetch, no questionnaire, nothing to sign, and no
 *    physician note (it is written for the person signing). The bridge
 *    refuses the form itself to this slot (409 `fee_only`).
 * The bridge refuses the form itself with a 409 code (`fee_required`,
 * `fee_declined`, `request_closed`) on the fields-schema, the PDF and the
 * upload; the page routes on it. On `fee_required` at the upload the patient
 * goes back to FEE with what they entered and signed KEPT here, and the form
 * is re-sent as soon as the card is saved.
 */

import { PLAN_MEMBER_ATTESTATION_TEXT } from "./signingStatements";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import OnboardingWizard from "./OnboardingWizard";
import BridgeFieldsForm from "./BridgeFieldsForm";
import PlanMemberJourney, { JourneyFrame, PlanMemberDone } from "./PlanMemberJourney";
import FeeStage from "./FeeStage";
import { FeeDeclinedNotice, RequestClosedNotice } from "./FeeDeclined";
import ProgressTimeline from "../../shared/ui/journey/ProgressTimeline";
import { PATIENT_FEE_STEP, patientSteps, type JourneyStep } from "../../shared/lib/journeySteps";
import PatientConfirmDetails from "./PatientConfirmDetails";
import DobChallenge from "./DobChallenge";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import { J } from "../../shared/ui/journey/journeyTheme";
import {
  clearBridgeSession,
  fetchEncryptedProfile,
  fetchPaymentStatus,
  formRefusalCodeOf,
  getBridgeSession,
  openSecretBox,
  sealSubmission,
  submitSealedToBridge,
  type FormRefusalCode,
} from "../../lib/bridgeClient";
import { buildConsentRecord } from "../../lib/consent";
import { canonicalizeProfileNumbers } from "../../lib/canonicalNumbers";
import {
  FEE_REQUIRED_LINE,
  REQUEST_CLOSED_BODY,
  canPayFromLink,
  cardSavedStrip,
  feeOnlyDoneCopy,
  feeRowLabel,
  feeStepApplies,
  paymentRoleAllowed,
} from "../../lib/paymentDisplay";
import type { PatientPaymentStatus, PatientProfile, PatientInsurance } from "../../types/shared";

// The camelCase wizard-profile dict this page passes around (built by the
// _*ToWizardProfile helpers below; consumed by OnboardingWizard/BridgeFieldsForm).
// Kept as a loose record — the keys are messy and cross snake/camel boundaries.
type WizardProfileDict = Record<string, any>;

// Bridge session shape (getBridgeSession is typed BridgeSession | null in
// bridgeClient; the extra flow keys — role, physicianNote, formId, the
// challenge hashes — arrive through its index signature as unknown).
type Session = ReturnType<typeof getBridgeSession>;

export { PLAN_MEMBER_ATTESTATION_TEXT } from "./signingStatements";

interface PlanMemberAttestation {
  text: string;
  accepted_at: string;
}

const STAGE = {
  DOB: "dob",
  VERIFY: "verify",
  WIZARD: "wizard",
  FEE: "fee",
  FIELDS: "fields",
  SUBMITTING: "submitting",
  DONE: "done",
  // The patient chose "I have decided not to have this form filled out" (G20).
  DECLINED: "declined",
  // Another signer's view of a request the patient closed (never why).
  CLOSED: "closed",
  // The end of a fee-only link (G23(f)): the fee step is done.
  FEE_DONE: "fee_done",
  ERROR: "error",
};

/** The rail of a fee-only link: the fee, then done. */
const FEE_ONLY_STEPS: JourneyStep[] = [PATIENT_FEE_STEP, { key: "done", label: "Done" }];

/** What the patient entered and signed, kept while the bridge refuses the
 *  upload for its fee (409 `fee_required`) and re-sent once the card is
 *  saved, so the questionnaire is never filled twice. */
interface HeldSubmission {
  fieldValues: Record<string, string> | null | undefined;
  signature: any;
  profilePatch: any;
}

/** How many times in a row the bridge may refuse the form for its fee
 *  without the fee step being shown in between before the page stops
 *  re-sending and says so (the bridge should never disagree with itself,
 *  so this is a loop guard, not a path). */
const MAX_FEE_BOUNCES = 2;

export default function BridgeOnboardingPage() {
  const session = useMemo(() => getBridgeSession(), []);
  // Derived from the server's flag now, not from the presence of a hash the
  // SPA no longer receives (case imp_bad9596e).
  const hasIdentityChallenge = !!session?.identityRequired;
  // Split-link two-signer flow: when this session belongs to the plan-member
  // signer slot (not the patient), skip the patient-profile verify/wizard
  // stages — the plan member doesn't need to confirm patient demographics.
  // Their role-banner explains the context and they go DOB → FIELDS directly.
  const isPlanMember = session?.role === "plan_member";
  // The physician's own preview of the link ("Test to me" / demo mint). The
  // bridge sync acknowledges such a submission without applying it, so the
  // page must say so up front and again at the end.
  const isSelfTest = !!session?.selfTest;
  // The patient's fee-only link (G23(f)): the fee step and nothing else.
  const isFeeOnly = !!session?.feeOnly && !isPlanMember && !isSelfTest;
  // The physician's note is written for the person filling and signing the
  // form (reminders say "waiting for your review and signature"); a
  // fee-only link has neither, so it is not shown there.
  const physicianNote = (isFeeOnly ? null : session?.physicianNote || null) as ReactNode;
  const [stage, setStage] = useState(
    hasIdentityChallenge ? STAGE.DOB : isFeeOnly ? STAGE.FEE : STAGE.VERIFY
  );
  const [verifiedDob, setVerifiedDob] = useState<string | null>(null);
  const [wizardProfile, setWizardProfile] = useState<WizardProfileDict | null>(null);
  const [preloadedProfile, setPreloadedProfile] = useState<PatientProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Patient payments (slice 3a). The server's status call is the only source
  // of truth for the fee step — stage is plain React state, nothing is kept in
  // storage — so a reload simply asks again on the way to the form.
  //   payment:     the last row the bridge returned (null = no row / no call)
  //   feeShown:    FeeStage was rendered this session (the done screen then
  //                shows the patient rail; Back from the form returns to it
  //                only while the row is still `pending`)
  //   feeMessage:  the one line shown above the questionnaire after the fee
  //                check (a choice's confirmation, or the card-saved strip)
  //   feeCheck:    this ENTRY's status call: "checking" until it answers,
  //                "show" (FeeStage renders), "error" (hold here with a
  //                retry). Reset on every way into FEE (`goToFee`), so the
  //                step is never stuck on a spinner from an earlier answer:
  //                the old `feeChecked` flag never reset, and Back from the
  //                form then forward again spun forever (case
  //                imp_03a4a18b).
  //   feeNotice:   the one line above FeeStage when the bridge refused the
  //                form for its fee (`fee_required`)
  const [payment, setPayment] = useState<PatientPaymentStatus | null>(null);
  const [feeShown, setFeeShown] = useState(false);
  const [feeMessage, setFeeMessage] = useState<string | null>(null);
  const [feeCheck, setFeeCheck] = useState<"idle" | "checking" | "show" | "error" | "expired">("idle");
  const [feeCheckSeq, setFeeCheckSeq] = useState(0);
  const [feeNotice, setFeeNotice] = useState<string | null>(null);
  const heldRef = useRef<HeldSubmission | null>(null);
  const bouncesRef = useRef(0);
  const aliveRef = useRef(true);
  // The stage as last rendered, for an answer that lands after the patient
  // may have moved on (`checkDeclinedEarly`).
  const stageRef = useRef(stage);
  stageRef.current = stage;
  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    []
  );

  // Fetch the encrypted profile once the SPA has a session AND the recipient
  // has proved who they are. The bridge sends it sealed under the
  // per-assignment symmetric key, so only this tab can decrypt it — but it
  // used to be FETCHED while the DOB screen was still on the page, keyed on
  // session presence rather than stage. The endpoint now refuses an unverified
  // session anyway (401 identity_required); waiting for the stage keeps the SPA
  // from firing a request it knows will be rejected.
  useEffect(() => {
    if (!session?.sessionToken) return;
    if (stage === STAGE.DOB) return;
    // A fee-only link shows no details, so it fetches none (G23(f)).
    if (isFeeOnly) return;
    let cancelled = false;
    fetchEncryptedProfile()
      .then((p) => {
        if (cancelled) return;
        setPreloadedProfile(p);
        setProfileLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setProfileLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.sessionToken, stage]);

  // If we landed on VERIFY but the profile fetch came back empty, fall
  // through to the full wizard. Doing this in an effect avoids a
  // setState-during-render warning.
  //
  // Plan-member signers skip both the patient-style VERIFY card and the
  // full patient OnboardingWizard — they only need to sign the consent
  // section, so we synthesize a minimal "wizard profile" from their
  // preloaded plan-member demographics and jump straight to FIELDS.
  useEffect(() => {
    if (stage !== STAGE.VERIFY) return;
    if (isPlanMember) {
      const seed = preloadedProfile
        ? _planMemberProfileToWizardProfile(preloadedProfile, verifiedDob)
        : verifiedDob ? { dateOfBirth: verifiedDob } : {};
      setWizardProfile(seed);
      setStage(STAGE.FIELDS);
      return;
    }
    // Returning patients (onboarding_complete=true) have already filled the
    // long form on a prior dispatch. Rather than jump straight to FIELDS,
    // show them a quick "anything changed?" confirm card (PatientConfirmDetails)
    // so they can review/correct demographics before signing — then continue.
    // Falls through to FIELDS only if the profile is somehow too sparse to review.
    if (profileLoaded && preloadedProfile && preloadedProfile.onboarding_complete) {
      if (_profileIsReviewable(preloadedProfile)) {
        return; // stay on VERIFY → PatientConfirmDetails renders
      }
      setWizardProfile(_macProfileToWizardProfile(preloadedProfile, verifiedDob));
      advanceFromDetails();
      return;
    }
    // We already have their core demographics on file (name + DOB + address) —
    // show the physician-style "Confirm Details" card (PatientConfirmDetails).
    // The patient reviews/edits their info + confirms insurance and provincial
    // coverage, instead of re-typing the whole 20-step wizard. Staying on
    // VERIFY lets that card render.
    if (profileLoaded && preloadedProfile && _profileIsReviewable(preloadedProfile)) {
      return;
    }
    // First-timers, or only the invite-minimum on file (first/last/email/DOB —
    // too sparse to review), fall through to the full WIZARD to collect the rest.
    if (profileLoaded) {
      setStage(STAGE.WIZARD);
    }
  }, [stage, profileLoaded, preloadedProfile, isPlanMember, verifiedDob]);

  // FEE: ask the bridge whether this form's payment row wants the fee step,
  // on EVERY entry (`goToFee` resets the answer). Only `pending` (and not
  // paused) renders FeeStage. 404 (no row: shadow sends, no-fee sends,
  // previews), 503 (not configured), a paused row (200 with paused: true —
  // no patient line) and every other status go on to the form; the bridge
  // does not refuse the form in any of those cases either. A
  // `declined_old_way` row goes to the closing page. Any OTHER failure of
  // the call (a network error, a 5xx) holds here with a retry: the bridge
  // would refuse the form to a patient who has not saved a card, so failing
  // open would only cost them the questionnaire. A `deferred` row is not
  // re-offered the step (no nagging, principle 16); the status page carries
  // its "Pay the fee".
  useEffect(() => {
    if (stage !== STAGE.FEE) return;
    let cancelled = false;
    setFeeCheck("checking");
    fetchPaymentStatus().then((r) => {
      if (cancelled) return;
      if (r.kind === "error") {
        // A 401 is the session (expired, or never verified), not the
        // network: a retry can never pass it, so the patient is sent back
        // to their newest link instead (review of PR #508).
        setFeeCheck(r.status === 401 ? "expired" : "error");
        return;
      }
      if (r.kind !== "ok") {
        // A fee-only link has nothing to go on to: a fee step that cannot
        // run (no row, payments not configured) holds here with the retry,
        // never a closing page that says nothing more is needed (review of
        // PR #535, F5).
        if (isFeeOnly) {
          setFeeCheck("error");
          return;
        }
        proceedPastFee();
        return;
      }
      const p = r.payment;
      setPayment(p);
      if (p.status === "declined_old_way") {
        heldRef.current = null;
        setStage(STAGE.DECLINED);
        return;
      }
      if (isFeeOnly && p.paused) {
        // Paused (Parseo's payments switch): the same hold, the same retry.
        setFeeCheck("error");
        return;
      }
      // A fee-only link also shows the pay-now screen of a card declined
      // right before the send: there is no status page in between (G23(f)).
      if (feeStepApplies(p) || (isFeeOnly && canPayFromLink(p))) {
        setFeeShown(true);
        bouncesRef.current = 0;
        setFeeCheck("show");
        return; // stay on FEE → FeeStage renders
      }
      if (p.status === "card_saved") setFeeMessage(cardSavedStrip(p));
      proceedPastFee();
    });
    return () => {
      cancelled = true;
    };
    // The check runs once per entry (stage) and per retry (feeCheckSeq).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, feeCheckSeq]);

  // A patient who already decided not to have the form filled out goes
  // straight to the closing page, not through their details again (G8).
  // Asked once: right after the date of birth when the link asks for it
  // (DobChallenge's onPass), and on arrival when it does not. A link whose
  // request carries no identity hash is issued an already-verified session
  // (`bridge/routers/magic.py`), so the status route answers at once (review
  // of PR #508). Only a declined row moves anything, and only while the
  // patient is still on their details; every other answer, and every later
  // stage, is left to the FEE check and the bridge's refusals.
  function checkDeclinedEarly() {
    if (!session?.sessionToken || !feeStepRole()) return;
    fetchPaymentStatus().then((r) => {
      if (!aliveRef.current) return;
      if (r.kind !== "ok" || r.payment.status !== "declined_old_way") return;
      if (stageRef.current !== STAGE.VERIFY && stageRef.current !== STAGE.WIZARD) return;
      heldRef.current = null;
      setPayment(r.payment);
      setStage(STAGE.DECLINED);
    });
  }

  useEffect(() => {
    // A fee-only link starts at FEE, whose own check routes a declined row.
    if (!hasIdentityChallenge && !isFeeOnly) checkDeclinedEarly();
    // Once, on arrival; the identity-challenge path asks in onPass instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every way into FEE goes through here, so each entry asks the bridge
  // afresh and never renders an earlier entry's answer.
  function goToFee() {
    setFeeCheck("idle");
    setStage(STAGE.FEE);
  }

  // Where a patient goes after their details. The plan member never comes
  // through here (their auto-advance above goes straight to FIELDS), and the
  // physician's own preview never enters the fee step. Only the roles the
  // payment endpoints serve (legacy, patient, or none) ask at all.
  function feeStepRole(): boolean {
    return !isPlanMember && !isSelfTest && paymentRoleAllowed(session?.role);
  }

  function advanceFromDetails() {
    if (feeStepRole()) goToFee();
    else setStage(STAGE.FIELDS);
  }

  // Past the fee step: re-send what the patient already entered and signed
  // when the bridge refused it for its fee, else on to the form.
  function proceedPastFee() {
    // A fee-only link has no form to go on to: the fee step was its whole
    // job (G23(f)).
    if (isFeeOnly) {
      setStage(STAGE.FEE_DONE);
      return;
    }
    const held = heldRef.current;
    if (held) {
      void handleFieldsComplete(held.fieldValues, held.signature, held.profilePatch);
      return;
    }
    setStage(STAGE.FIELDS);
  }

  // The bridge refused the form itself for its fee (the fields-schema, the
  // PDF or the upload; ruling 2026-10-14). `held` is what the patient had
  // entered and signed, when the refusal came at the upload.
  function handleFormRefused(code: FormRefusalCode, held?: HeldSubmission) {
    if (code === "fee_declined") {
      heldRef.current = null;
      setPayment((p) => (p ? { ...p, status: "declined_old_way" } : p));
      setStage(STAGE.DECLINED);
      return;
    }
    if (code === "request_closed" || !feeStepRole()) {
      heldRef.current = null;
      setStage(STAGE.CLOSED);
      return;
    }
    // fee_required: back to the fee step, keeping what was entered.
    if (held) heldRef.current = held;
    bouncesRef.current += 1;
    if (bouncesRef.current > MAX_FEE_BOUNCES) {
      setError("fee_required");
      setStage(STAGE.ERROR);
      return;
    }
    setFeeNotice(FEE_REQUIRED_LINE);
    goToFee();
  }

  // No bridge session → kick back to the root, which will show
  // either the normal Supabase login or the "welcome" page.
  if (!session) {
    return <Navigate to="/" replace />;
  }

  function handleWizardComplete(data: any) {
    const profile = (data && data.profile) || data || {};
    // Mark the patient as having completed the long onboarding so future
    // dispatches skip directly to FIELDS. _normalizeProfile preserves these
    // keys via snake_case in the submission, and bridge_sync pushes them
    // back to the Mac's patient_profiles.json on /api/patient/profile.
    const marked = {
      ...profile,
      onboarding_complete: true,
      onboarding_completed_at: new Date().toISOString(),
    };
    setWizardProfile(marked);
    // Always advance to the fields stage. BridgeFieldsForm decides whether
    // to render the side-panel UI (when fields-schema is non-empty) or
    // skip straight through to submit (when the assignment has no
    // patient-fill fields). Patients pass the fee check on the way.
    advanceFromDetails();
  }

  async function handleFieldsComplete(
    fieldValues: Record<string, string> | null | undefined,
    signatureFromPatient: any,
    profilePatch: any
  ) {
    if (!wizardProfile) {
      setError("Onboarding state lost — please refresh and try again.");
      setStage(STAGE.ERROR);
      return;
    }
    setStage(STAGE.SUBMITTING);
    try {
      const profile = wizardProfile;
      const submittedAt = new Date().toISOString();
      // Prefer the freshly-captured signature from this submission; fall
      // back to whatever was on file in the profile.
      const sigName =
        (signatureFromPatient && signatureFromPatient.name) ||
        profile.signatureName ||
        `${profile.firstName || profile.first_name || ""} ${profile.lastName || profile.last_name || ""}`.trim();
      const sigFont =
        (signatureFromPatient && signatureFromPatient.font) ||
        profile.signatureFont || profile.signature_font || null;
      // Drawn mark (PNG data URL) when the patient picked Draw mode in
      // BridgeFieldsForm. Travels alongside the legal name so future PDF
      // compositing can render the actual ink stroke on the signature line.
      const sigImageB64 =
        (signatureFromPatient && signatureFromPatient.image_b64) || null;
      const sigSource =
        (signatureFromPatient && signatureFromPatient.source) ||
        (signatureFromPatient ? "patient_typed" : "profile_default");

      // Base profile (snake_case) from the wizard's camelCase keys.
      let normalizedProfile = _normalizeProfile(profile, verifiedDob);

      // Overlay the ProfileEditor edits when present. The patch already
      // contains the FULL profile dict (initial + edits), so we can take
      // the patient-editable keys directly. update_my_file rides along to
      // tell the bridge_sync whether to push the update back to the Mac.
      let updateMyFile = true;
      if (profilePatch && profilePatch.profile) {
        normalizedProfile = {
          ...normalizedProfile,
          ..._extractEditableSnakeKeys(profilePatch.profile),
        };
        updateMyFile = profilePatch.update_my_file !== false;
      }

      // Numbers go out canonical — no spaces, no hyphens. The health-card
      // input is masked as the patient types, so without this the portal
      // submitted "0015 520 174" against a stored "0015520174" and the Mac
      // queued a physician-review chip for a formatting difference
      // (imp_11cf2120). One choke point for the whole bridge flow: it covers
      // both the wizard's own values and the ProfileEditor overlay above.
      normalizedProfile = canonicalizeProfileNumbers(normalizedProfile);

      const submission = {
        // Prefer the assignment's authoritative patient_id (carried on the
        // preloaded profile) so the submission lands on the existing record.
        // Fall back to the wizard profile id, then a synthesized id only for
        // brand-new patients with no record yet.
        patient_id:
          preloadedProfile?.patient_id ||
          profile.id || profile.patientId || profile.patient_id ||
          `${profile.firstName || profile.first_name || "unknown"}-${profile.lastName || profile.last_name || "unknown"}-${profile.dateOfBirth || profile.date_of_birth || verifiedDob || "unknown"}`
            .toLowerCase()
            .replace(/\s+/g, "-"),
        form_id: session!.formId || null,
        profile: normalizedProfile,
        // Surface the patient's preference at the top of the submission so
        // bridge_sync can read it without prying into the profile dict.
        update_my_file: updateMyFile,
        // Consent: when the patient ticked the single acknowledgement in the
        // wizard this session, record a fresh granular record (flags + version
        // + timestamp). When the wizard was skipped (returning patient, or the
        // "confirm what's on file" path), carry their existing consent block
        // forward verbatim rather than re-stamping a consent they didn't
        // actively re-give. The dispatch-time consent gate guarantees a prior
        // record exists for the skip path, so the final fallback is defensive.
        consent: profile.consent_accepted
          ? buildConsentRecord({ accepted: true, signedAt: submittedAt })
          : preloadedProfile?.consent ||
            // Defensive fallback only (the dispatch-time consent gate should
            // guarantee a prior record for the skip path). Since v7 every key,
            // de-identified use included, follows the single acknowledgement,
            // so there is no separate choice left to carry here.
            buildConsentRecord({
              accepted: true,
              signedAt: submittedAt,
            }),
        field_values: fieldValues || {},
        // The plan member's attestation (their leg has no identity challenge):
        // the statement their signing checkbox carried, and when they signed.
        // plan_member_signed_at mirrors submitted_at, which bridge_sync
        // already reads for the tracker's "Plan holder signed" step.
        ...(isPlanMember
          ? {
              attestation: { text: PLAN_MEMBER_ATTESTATION_TEXT, accepted_at: submittedAt } as PlanMemberAttestation,
              plan_member_signed_at: submittedAt,
            }
          : {}),
        signature: {
          authorization_signature: sigName,
          signature_font: sigFont,
          signature_image_b64: sigImageB64,
          signed_at: submittedAt,
          signature_source: sigSource,
        },
        submitted_at: submittedAt,
      };

      const ciphertextB64 = sealSubmission(submission, session!.pubkeyB64 as string);
      await submitSealedToBridge({
        assignmentId: session!.assignmentId as string,
        ciphertextB64,
      });
      heldRef.current = null;
      setFeeNotice(null);
      clearBridgeSession();
      setStage(STAGE.DONE);
    } catch (err) {
      const refusal = formRefusalCodeOf(err);
      if (refusal) {
        // Nothing was stored. Keep what the patient entered and signed for
        // the re-send (fee_required), or end on the page that says why not.
        handleFormRefused(refusal, { fieldValues, signature: signatureFromPatient, profilePatch });
        return;
      }
      console.error("bridge submit failed:", err);
      setError((err as Error).message || String(err));
      setStage(STAGE.ERROR);
    }
  }

  if (stage === STAGE.DOB) {
    return (
      <RoleBannerWrap isPlanMember={isPlanMember} isSelfTest={isSelfTest} preloadedProfile={preloadedProfile} physicianNote={physicianNote}>
        <DobChallenge
          // Challenge fields arrive through the bridge session's index
          // signature typed `unknown` (see Session note above); narrow them
          // to the shapes DobChallenge expects.
          identityFactors={session.identityFactors as string[] | undefined}
          onPass={(dob: any) => {
            setVerifiedDob(dob);
            if (isFeeOnly) {
              // Straight to the fee: no details to confirm (G23(f)). The FEE
              // check routes a row that already reads declined.
              goToFee();
              return;
            }
            setStage(STAGE.VERIFY);
            checkDeclinedEarly();
          }}
          onFail={() => {
            // The DobChallenge has already cleared the session; just rerender.
          }}
        />
      </RoleBannerWrap>
    );
  }

  if (stage === STAGE.VERIFY) {
    // If profile fetch hasn't completed yet, wait. If it completed with no
    // profile (new patient, or older Mac that doesn't push profiles), the
    // effect above flips us to WIZARD — render the spinner until that lands.
    if (!profileLoaded || !preloadedProfile) {
      return _loadingView("Loading your information…");
    }
    return (
      <PatientConfirmDetails
        macProfile={preloadedProfile}
        verifiedDob={verifiedDob}
        onConfirm={(mergedProfile) => {
          // `mergedProfile` is the full snake_case dict (preloaded + the
          // patient's edits, with the insurance sub-doc + flat keys + the
          // provincial-coverage flag kept in sync). _macProfileToWizardProfile
          // carries every edit through to the FIELDS stage and submission.
          const mac = mergedProfile || preloadedProfile;
          const wiz = _macProfileToWizardProfile(mac, verifiedDob);
          // Preserve the returning-patient marker — confirming details on the
          // "anything changed?" card must NOT reset onboarding_complete (else
          // the next dispatch treats them as a first-timer again).
          if (preloadedProfile.onboarding_complete) {
            wiz.onboarding_complete = true;
            wiz.onboarding_completed_at =
              preloadedProfile.onboarding_completed_at || new Date().toISOString();
          }
          setWizardProfile(wiz);
          advanceFromDetails();
        }}
      />
    );
  }

  if (stage === STAGE.WIZARD) {
    const initial = preloadedProfile
      ? _macProfileToWizardProfile(preloadedProfile, verifiedDob)
      : verifiedDob
      ? { dateOfBirth: verifiedDob }
      : {};
    return (
      <OnboardingWizard
        bridgeMode={true}
        initialProfile={initial}
        onComplete={handleWizardComplete}
      />
    );
  }

  if (stage === STAGE.FEE) {
    const backToDetails = () => setStage(preloadedProfile ? STAGE.VERIFY : STAGE.WIZARD);
    // A fee-only link has no details to go back to, and its rail is the fee
    // then done (G23(f)). A card declined right before the send is paid on
    // the same screen in pay mode, whose "Not now" ends on FEE_DONE.
    const feeSteps = isFeeOnly ? FEE_ONLY_STEPS : patientSteps(true);
    const feeMode: "fee" | "pay" = isFeeOnly && canPayFromLink(payment) ? "pay" : "fee";
    if (feeCheck === "expired") {
      // The bridge no longer accepts this session (401). The status page's
      // own words for it; no retry, because none can pass.
      return _centered(
        <section aria-labelledby="fee-check-expired-heading" data-testid="fee-check-expired">
          <ParseoLogo size={56} />
          <h2 id="fee-check-expired-heading" style={{ color: "#182a33", margin: "16px 0 6px" }}>
            Session expired
          </h2>
          <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.5, margin: 0 }}>
            Tap the link in your most recent text or email from Parseo Health to get back in.
          </p>
        </section>
      );
    }
    if (feeCheck === "error") {
      // The status call failed (not a 404, a 401 or a 503): hold here with a
      // retry rather than open a form the bridge would refuse. The heading
      // does not assert a fee: the call failed, so the page does not know.
      return (
        <RoleBannerWrap isPlanMember={isPlanMember} isSelfTest={isSelfTest} preloadedProfile={preloadedProfile} physicianNote={physicianNote}>
          <JourneyFrame currentKey="fee" steps={feeSteps}>
            <section aria-labelledby="fee-check-error-heading" data-testid="fee-check-error">
              {/* DRAFT (2026-10-14; heading softened 2026-10-15, review of PR #508). */}
              <h1 id="fee-check-error-heading" style={{ fontSize: 22, color: J.ink, margin: "6px 0 8px" }}>
                We couldn't open your form just now
              </h1>
              <p style={{ fontSize: 15, color: J.body, lineHeight: 1.5, margin: 0 }}>
                Check your connection, then try again. Nothing has been charged.
              </p>
              <div style={{ display: "grid", gap: 10, marginTop: 16 }}>
                <button
                  type="button"
                  style={_primaryButton}
                  onClick={() => {
                    setFeeCheck("idle");
                    setFeeCheckSeq((n) => n + 1);
                  }}
                >
                  Try again
                </button>
                {isFeeOnly ? null : (
                  <button type="button" style={_linkButton} onClick={backToDetails}>
                    Back to my details
                  </button>
                )}
              </div>
            </section>
          </JourneyFrame>
        </RoleBannerWrap>
      );
    }
    if (feeCheck !== "show" || !payment
        || (feeMode === "pay" ? !canPayFromLink(payment) : payment.status !== "pending")) {
      return _loadingView("Opening your form…");
    }
    return (
      <RoleBannerWrap isPlanMember={isPlanMember} isSelfTest={isSelfTest} preloadedProfile={preloadedProfile} physicianNote={physicianNote}>
        <JourneyFrame currentKey="fee" steps={feeSteps}>
          {feeNotice ? (
            <p
              data-testid="fee-required-line"
              role="status"
              style={{ fontSize: 14.5, color: J.ink, lineHeight: 1.45, margin: "0 0 12px" }}
            >
              {feeNotice}
            </p>
          ) : null}
          <FeeStage
            payment={payment}
            mode={feeMode}
            onBack={isFeeOnly ? (feeMode === "pay" ? () => setStage(STAGE.FEE_DONE) : undefined) : backToDetails}
            onDecline={() => {
              heldRef.current = null;
              setPayment({ ...payment, status: "declined_old_way" });
              setStage(STAGE.DECLINED);
            }}
            onContinue={(outcome, message, latest) => {
              setPayment(
                latest || {
                  ...payment,
                  status: outcome === "waiver" ? "waiver_requested" : outcome === "question" ? "question" : outcome === "card_saved" ? "card_saved" : "deferred",
                }
              );
              setFeeMessage(message);
              if (isFeeOnly) {
                // The fee step was the link's whole job (G23(f)).
                setStage(STAGE.FEE_DONE);
                return;
              }
              // The bridge refused the upload for its fee: what the patient
              // entered and signed is re-sent now, not asked for again.
              const held = heldRef.current;
              if (held) {
                void handleFieldsComplete(held.fieldValues, held.signature, held.profilePatch);
                return;
              }
              setStage(STAGE.FIELDS);
            }}
          />
        </JourneyFrame>
      </RoleBannerWrap>
    );
  }

  if (stage === STAGE.DECLINED) {
    // The decline (ruling 2026-10-14; wording G20, 2026-10-15): nothing
    // charged and the office told. No questionnaire, no tracker, no "what
    // happens next", no paper-form or printing instructions (Michael: "they
    // can't complete the form themselves"), and never "Download a copy"
    // (that is Parseo's prepared form).
    return _centered(
      <>
        <ParseoLogo size={56} />
        <FeeDeclinedNotice payment={payment} />
      </>
    );
  }

  if (stage === STAGE.CLOSED) {
    return _centered(
      <>
        <ParseoLogo size={56} />
        <RequestClosedNotice />
      </>
    );
  }

  if (stage === STAGE.FEE_DONE || (isFeeOnly && stage === STAGE.FIELDS)) {
    // The end of a fee-only link (G23(f)): the fee step is done and nothing
    // more is asked. Never the questionnaire, the prepared PDF or a
    // signature, whatever stage a stray transition asks for. DRAFT.
    const done = feeOnlyDoneCopy(payment);
    return _centered(
      <section aria-labelledby="fee-only-done-heading" data-testid="fee-only-done">
        <div style={{ width: "100%", maxWidth: 440, marginBottom: 18 }}>
          {/* Done only when it is: a fee still owed stays on the fee step. */}
          <ProgressTimeline steps={FEE_ONLY_STEPS} currentKey={done.nothingMore ? "done" : "fee"}
                            doneAll={done.nothingMore} />
        </div>
        <ParseoLogo size={56} />
        <h2 id="fee-only-done-heading" style={{ color: "#182a33", margin: "16px 0 6px" }}>
          {done.heading}
        </h2>
        {done.body ? (
          <p style={{ color: "#5b6e7c", maxWidth: 440, lineHeight: 1.5, margin: "0 0 6px" }}>
            {done.body}
          </p>
        ) : null}
        {done.nothingMore ? (
          <p style={{ color: "#5b6e7c", maxWidth: 440, lineHeight: 1.5, margin: 0 }}>
            {REQUEST_CLOSED_BODY}
          </p>
        ) : null}
      </section>
    );
  }

  if (stage === STAGE.FIELDS) {
    const defaultSig =
      wizardProfile?.signatureName ||
      [wizardProfile?.firstName, wizardProfile?.lastName].filter(Boolean).join(" ") ||
      "";
    // Build the editor profile from the preloaded snake_case profile, then
    // merge the wizard's answers OVER it (non-empty only). Without the merge,
    // anything the patient entered/changed in the wizard that the stored
    // profile lacked (e.g. sex, member ID, plan #) silently fails to show on
    // the review screen — the editor would render only the stale preloaded data.
    const wizardDerived = _wizardProfileToMacProfile(wizardProfile, verifiedDob);
    let profileForEditor: Record<string, any>;
    if (preloadedProfile) {
      profileForEditor = { ...preloadedProfile };
      for (const [k, v] of Object.entries(wizardDerived)) {
        if (k === "insurance" && v && typeof v === "object") {
          const ins: Record<string, any> = { ...(preloadedProfile.insurance || {}) };
          for (const [ik, iv] of Object.entries(v)) if (iv) ins[ik] = iv;
          profileForEditor.insurance = ins;
        } else if (v !== "" && v != null) {
          profileForEditor[k] = v;
        }
      }
    } else {
      profileForEditor = wizardDerived;
    }
    // The plan holder's leg runs on the shared journey components: welcome,
    // their details, the questions still open, sign. Same schema, seeding,
    // emit logic and submit as below; only the screens differ.
    if (isPlanMember) {
      return (
        <RoleBannerWrap isPlanMember={isPlanMember} isSelfTest={isSelfTest} preloadedProfile={preloadedProfile} physicianNote={physicianNote}>
          <PlanMemberJourney
            session={session}
            initialProfile={profileForEditor}
            defaultSignatureName={defaultSig}
            defaultSignatureFont={wizardProfile?.signatureFont || "Dancing Script"}
            onSubmit={(fieldValues, signature) => handleFieldsComplete(fieldValues, signature, null)}
            onRefused={(code) => handleFormRefused(code)}
          />
        </RoleBannerWrap>
      );
    }
    // Demographics review already happened up front — on the physician-style
    // PatientConfirmDetails card (preloaded patients), in the full
    // OnboardingWizard (first-timers), or on a prior dispatch (returning
    // patients). So BridgeFieldsForm always lands on the fillable form rather
    // than re-showing the "Review your information" editor as a second
    // mandatory step. The editor stays available on demand: the "Edit my info"
    // link in the panel and click-to-fix on the PDF both reopen it.
    return (
      <RoleBannerWrap isPlanMember={isPlanMember} isSelfTest={isSelfTest} preloadedProfile={preloadedProfile} physicianNote={physicianNote}>
        {feeMessage ? (
          <div
            data-testid="fee-strip"
            role="status"
            aria-live="polite"
            style={{
              backgroundColor: "#e8f1ee",
              borderBottom: "1px solid #c8deda",
              color: "#25333b",
              padding: "10px 16px",
              textAlign: "center",
              fontSize: 14,
              lineHeight: 1.4,
            }}
          >
            {feeMessage}
          </div>
        ) : null}
        <BridgeFieldsForm
          onComplete={handleFieldsComplete}
          defaultSignatureName={defaultSig}
          defaultSignatureFont={wizardProfile?.signatureFont || "Dancing Script"}
          initialProfile={profileForEditor as any}
          skipProfileReview={true}
          onRefused={(code) => handleFormRefused(code)}
          onBack={() => {
            // Back to the fee step only while the row is still pending: a
            // waiver, question, deferral or saved card is never re-armed.
            if (feeShown && payment?.status === "pending") goToFee();
            else setStage(preloadedProfile ? STAGE.VERIFY : STAGE.WIZARD);
          }}
        />
      </RoleBannerWrap>
    );
  }

  if (stage === STAGE.SUBMITTING) {
    return _loadingView("Sending your form…", "Encrypting and submitting securely.");
  }

  if (stage === STAGE.DONE && isSelfTest) {
    // The physician's own preview: the bridge sync acknowledges this
    // submission without writing anything, so say so.
    return <PlanMemberDone selfTest showTimeline={isPlanMember} />;
  }

  if (stage === STAGE.DONE && isPlanMember) {
    // The plan member's part is done; the rest of the request is the
    // patient's, so no PA tracker and no promise of updates.
    return <PlanMemberDone selfTest={false} />;
  }

  if (stage === STAGE.DONE) {
    // Nothing here changes for a form with no payment row: the rail and the
    // fee line render only when the fee step was shown / a row came back.
    const feeRow = feeRowLabel(payment);
    return _centered(
      <>
        {feeShown ? (
          <div style={{ width: "100%", maxWidth: 440, marginBottom: 18 }} data-testid="patient-rail">
            <ProgressTimeline steps={patientSteps(true)} currentKey="done" doneAll />
          </div>
        ) : null}
        <ParseoLogo size={56} />
        <h2 style={{ color: "#182a33", margin: "16px 0 4px" }}>
          Submitted — thank you.
        </h2>
        <p style={{ color: "#5b6e7c", maxWidth: 440, lineHeight: 1.5, margin: 0 }}>
          Your form has been securely sent to your provider for review.
        </p>
        <_PaTracker />
        {feeRow ? (
          <p data-testid="fee-done-line" style={{ fontSize: 13.5, color: "#46685d", margin: "4px 0 0", fontWeight: 600 }}>
            Fee: {feeRow.label}
          </p>
        ) : null}
        <button
          onClick={() => _downloadForm(session)}
          style={{
            marginTop: 18, padding: "10px 20px", borderRadius: 8,
            border: "1px solid #d8c9b6", background: "#fff", color: "#182a33",
            fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}
        >
          Download a copy for your records
        </button>
        <p style={{ fontSize: 12.5, color: "#5b6e7c", marginTop: 16, maxWidth: 440, lineHeight: 1.55 }}>
          What happens next: we'll text and email you when your doctor sends
          this to your insurer, and again when there's a decision. If your
          insurer mails you a letter, the link in those messages lets you send
          it straight to your doctor's office.
        </p>
      </>
    );
  }

  // STAGE.ERROR
  return _centered(
    <>
      <h2 style={{ color: "#c25450", marginBottom: 8 }}>Submission failed</h2>
      <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.5 }}>
        Something went wrong sending your form. Please try again later or
        contact your provider for a fresh link.
      </p>
      {error && (
        <p style={{ color: "#999", fontSize: 12, maxWidth: 420 }}>
          Detail: {error}
        </p>
      )}
    </>
  );
}


/**
 * Renders a thin amber banner above its children when the recipient is the
 * plan-member signer slot — gives context ("You are signing on behalf of
 * {patient_first_name}'s coverage request") so a spouse or parent doesn't
 * wonder why the form has someone else's clinical details.
 *
 * Also renders the physician note card (when present) below the role banner
 * and above the form content. The note card is shown for ALL recipients
 * (patient, plan_member, legacy) when a note exists.
 */
interface RoleBannerWrapProps {
  isPlanMember: boolean;
  isSelfTest?: boolean;
  preloadedProfile: PatientProfile | null;
  physicianNote?: ReactNode;
  children: ReactNode;
}

function RoleBannerWrap({ isPlanMember, isSelfTest, preloadedProfile, physicianNote, children }: RoleBannerWrapProps) {
  const hasBanner = isPlanMember;
  const hasNote = Boolean(physicianNote);
  if (!hasBanner && !hasNote && !isSelfTest) return <>{children}</>;
  const patientFirst = preloadedProfile?.first_name || "the patient";
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f0eb" }}>
      {isSelfTest && (
        <div
          data-testid="self-test-banner"
          style={{
            backgroundColor: "#182a33",
            color: "#f5f0eb",
            padding: "10px 16px",
            textAlign: "center",
            fontSize: 14,
            lineHeight: 1.4,
          }}
        >
          <strong>Test link.</strong>{" "}
          This is your own preview of what the recipient sees. Nothing entered
          here is saved to the form, and nobody is notified.{" "}
          <span data-testid="self-test-no-payment">Test link, no payment is taken.</span>
        </div>
      )}
      {hasBanner && (
        <div
          style={{
            backgroundColor: "#fae4ca",
            borderBottom: "1px solid #e2c97a",
            padding: "10px 16px",
            textAlign: "center",
            color: "#5a4a1a",
            fontSize: 14,
            lineHeight: 1.4,
          }}
        >
          <strong>Plan member consent.</strong>{" "}
          You're providing consent as the plan member for {patientFirst}
          {'\''}s coverage request.
        </div>
      )}
      {hasNote && (
        <div
          style={{
            backgroundColor: "#eef4f3",
            borderBottom: "1px solid #c8deda",
            padding: "12px 20px",
            maxWidth: 560,
            margin: "0 auto",
            marginTop: 16,
            borderRadius: 8,
            border: "1px solid #c8deda",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: "#1b5e6e", marginBottom: 4 }}>
            A note from your provider:
          </div>
          <div style={{ fontSize: 14, color: "#25333b", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {physicianNote}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}


function _centered(content: ReactNode) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
        textAlign: "center",
      }}
    >
      {content}
    </div>
  );
}

const _primaryButton: React.CSSProperties = {
  width: "100%",
  minHeight: 50,
  border: 0,
  borderRadius: J.radiusButton,
  background: J.ink,
  color: J.white,
  fontSize: 16,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
};
const _linkButton: React.CSSProperties = {
  background: "none",
  border: 0,
  padding: "6px 0",
  color: J.teal,
  fontSize: 14,
  fontWeight: 600,
  fontFamily: "inherit",
  cursor: "pointer",
  textDecoration: "underline",
};

// Branded waiting view: the animated Parseo mosaic (tiles soft-pulse in a
// staggered clockwise ripple, reduced-motion aware) above a caption.
function _loadingView(caption: ReactNode, sub?: ReactNode) {
  return _centered(
    <>
      <ParseoLogo animated size={72} />
      <h2 style={{ color: "#182a33", margin: "16px 0 6px" }}>{caption}</h2>
      {sub && <p style={{ color: "#5b6e7c", margin: 0, maxWidth: 420 }}>{sub}</p>}
    </>
  );
}


// The PA lifecycle tracker shown on the patient's done page — the patient's
// part (sent → signed) is complete; the insurer steps are still ahead.
function _PaTracker() {
  const steps = [
    { n: 1, label: "Form Sent", done: true },
    { n: 2, label: "You Signed", done: true },
    { n: 3, label: "Faxed to Insurer", done: false },
    { n: 4, label: "Insurer Reviewed", done: false },
    { n: 5, label: "Decision", done: false },
  ];
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center",
                  flexWrap: "wrap", margin: "22px 0 6px" }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, width: 84 }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%", display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700,
              background: s.done ? "#6b9c8e" : "#e4dccd", color: s.done ? "#fff" : "#8a96a3",
            }}>{s.done ? "✓" : s.n}</div>
            <span style={{ fontSize: 11, lineHeight: 1.3, textAlign: "center",
                           color: s.done ? "#46685d" : "#8a96a3" }}>{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div style={{ width: 26, height: 2, marginTop: 15,
                          background: steps[i + 1].done ? "#6b9c8e" : "#e4dccd" }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Download a copy of the form for the patient's records: re-fetch + decrypt the
// dispatched PDF with the session's symmetric key (same path BridgeFieldsForm
// uses to render it), then trigger a browser download.
async function _downloadForm(session: Session) {
  try {
    // Prefer the bytes stashed during the flow (the re-fetch 401s once the
    // assignment is submitted/locked); fall back to a live fetch+decrypt.
    let pdfBytes: any =
      (typeof window !== "undefined" && (window as any).__hfFormPdfBytes) || null;
    if (!pdfBytes && session?.assignmentId && session?.symmetricKeyB64) {
      const resp = await fetch(
        `/api/bridge/assignments/${encodeURIComponent(session.assignmentId)}/pdf`,
        { method: "GET", headers: { "X-Patient-Token": session.sessionToken as string }, credentials: "omit" }
      );
      if (resp.ok) {
        const body = await resp.json();
        if (body.encrypted_b64) {
          pdfBytes = openSecretBox(body.encrypted_b64, session.symmetricKeyB64);
        }
      }
    }
    if (!pdfBytes) return;
    const url = URL.createObjectURL(new Blob([pdfBytes], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "prior-authorization-form.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) {
    console.warn("download form failed:", e);
  }
}

// Plan-member preload comes through the same /assignments/{id}/profile
// endpoint but the bridge serves the plan_member-slot subset. The keys are
// `plan_member_first_name` etc. plus the patient's `first_name` for
// banner copy. Synthesize a wizard profile so downstream components that
// expect camelCase work without changes.
function _planMemberProfileToWizardProfile(macProfile: PatientProfile | null, verifiedDob: string | null): WizardProfileDict {
  if (!macProfile) return verifiedDob ? { dateOfBirth: verifiedDob } : {};
  return {
    firstName: macProfile.plan_member_first_name || "",
    lastName: macProfile.plan_member_last_name || "",
    dateOfBirth: macProfile.plan_member_dob || verifiedDob || "",
    healthCardNumber: macProfile.plan_member_health_card_number || "",
    // Carry the patient's first name (display-only) for the role banner.
    patientFirstName: macProfile.first_name || "",
    isPlanMember: true,
  };
}


// True when the preloaded Mac profile already has enough on file to be worth
// reviewing on the quick "Quick check" card (vs. the full wizard). Requires the
// core identity plus a real address — i.e. more than the invite-minimum
// (first/last/email/DOB), which isn't usefully reviewable and should go to the
// wizard so the patient fills in the rest.
function _profileIsReviewable(p: PatientProfile | null): boolean {
  if (!p) return false;
  const core = !!(p.first_name && p.last_name && p.date_of_birth);
  const hasAddress = !!(p.address && (p.postal_code || p.city));
  return core && hasAddress;
}

function _macProfileToWizardProfile(macProfile: PatientProfile | null, verifiedDob: string | null): WizardProfileDict {
  if (!macProfile) return verifiedDob ? { dateOfBirth: verifiedDob } : {};
  // NB: we intentionally do not seed `consent_accepted` here. A returning
  // patient's prior consent is carried forward from preloadedProfile.consent
  // at submit time; a first-time/partial patient who enters the wizard must
  // actively tick the single acknowledgement.
  // Insurance lives in a structured sub-doc; fall back to legacy flat keys.
  // Without this, the "Your plan details" step renders empty even when the
  // plan/member/group IDs are already on file.
  const ins: PatientInsurance = macProfile.insurance || {};
  return {
    // Authoritative id from the assignment — keeps the patient's submission
    // on the SAME record the physician references (no synthesized duplicate).
    patientId: macProfile.patient_id || "",
    firstName: macProfile.first_name || "",
    lastName: macProfile.last_name || "",
    dateOfBirth: macProfile.date_of_birth || verifiedDob || "",
    gender: macProfile.gender || "",
    healthCardNumber: macProfile.health_care_number || "",
    email: macProfile.email || "",
    phone: macProfile.phone || "",
    streetAddress: macProfile.address || "",
    city: macProfile.city || "",
    province: macProfile.province || "NS",
    postalCode: macProfile.postal_code || "",
    insuranceCarrier: ins.provider || macProfile.insurance_provider || "",
    memberId: ins.member_id || macProfile.insurance_id || "",
    planNumber: ins.plan_number || macProfile.policy_number || macProfile.plan_number || "",
    groupNumber: ins.group_id || macProfile.group_number || macProfile.group_id || "",
    certificateNumber: ins.certificate_number || macProfile.certificate_number || "",
    planSponsor: ins.plan_sponsor || macProfile.plan_sponsor || "",
    // Whether the patient also has provincial drug coverage (Pharmacare etc.),
    // captured on the Confirm Details card. Pass the boolean through unchanged
    // (null = unanswered) so it round-trips back to the canonical profile.
    hasPublicInsurance: macProfile.has_public_insurance ?? null,
    // Secondary/supplemental private plan — preloaded so a prior answer shows
    // in the wizard. Boolean → the wizard's Yes/No choice value.
    hasSecondaryInsurance:
      macProfile.has_supplemental_insurance === true
        ? "Yes"
        : macProfile.has_supplemental_insurance === false
        ? "No"
        : "",
    secondaryInsuranceCarrier: macProfile.supplemental_insurance_provider || "",
    // Plan member (when the patient isn't the primary planholder). Round-trips
    // so a prior answer re-displays in the wizard's plan-member section.
    relationshipToPlanholder: macProfile.plan_member_relationship || ins.relationship || "self",
    planholderFirstName: macProfile.plan_member_first_name || "",
    planholderLastName: macProfile.plan_member_last_name || "",
    planholderDob: macProfile.plan_member_dob || "",
    planholderEmail: macProfile.plan_member_email || "",
    planholderPhone: macProfile.plan_member_phone || "",
    pharmacyName: macProfile.pharmacy_name || "",
    pharmacyFax: macProfile.pharmacy_fax || "",
    pharmacyLocation: macProfile.pharmacy_location || "",
    physicianFirstName: macProfile.physician_first_name || "",
    physicianLastName: macProfile.physician_last_name || "",
    physicianPhone: macProfile.physician_phone || "",
    physicianFax: macProfile.physician_fax || "",
    physicianCity: macProfile.physician_city || "",
    signatureName: macProfile.authorization_signature || "",
    signatureFont: macProfile.signature_font || "Dancing Script",
  };
}


// Patient-editable keys (must stay in sync with EDITABLE_KEYS in
// ProfileEditor.jsx and the patient-editable subset of
// _PROFILE_PRELOAD_KEYS in routers/bridge.py).
const _EDITABLE_PROFILE_KEYS = [
  "first_name", "last_name", "date_of_birth", "gender",
  "health_care_number", "email", "phone",
  "address", "city", "province", "postal_code",
  "insurance_provider", "insurance_id",
  // Policy/group number — distinct from the member id (insurance_id). Must be
  // submitted so the patient's plan/contract number actually persists.
  "plan_number", "group_number",
  // Whether the patient also has provincial drug coverage (Pharmacare etc.),
  // confirmed on the Confirm Details card. Boolean — false is a real answer,
  // so _extractEditableSnakeKeys carries it (only null/"" are dropped).
  "has_public_insurance",
];

function _extractEditableSnakeKeys(profile: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const k of _EDITABLE_PROFILE_KEYS) {
    if (profile[k] !== undefined && profile[k] !== null && profile[k] !== "") {
      out[k] = profile[k];
    }
  }
  return out;
}

// Inverse of _macProfileToWizardProfile — for the "no preloaded profile,
// patient went through the wizard" path. Lets ProfileEditor render even
// when the Mac never sent demographics for this patient.
function _wizardProfileToMacProfile(wp: WizardProfileDict | null, verifiedDob: string | null): Record<string, any> {
  if (!wp) return {};
  return {
    first_name: wp.firstName || "",
    last_name: wp.lastName || "",
    date_of_birth: wp.dateOfBirth || verifiedDob || "",
    gender: wp.gender || "",
    health_care_number: wp.healthCardNumber || "",
    email: wp.email || "",
    phone: wp.phone || "",
    address: wp.streetAddress || "",
    city: wp.city || "",
    province: wp.province || "",
    postal_code: wp.postalCode || "",
    insurance_provider: wp.insuranceCarrier || "",
    insurance_id: wp.memberId || "",
    has_public_insurance: wp.hasPublicInsurance ?? null,
    insurance: {
      provider: wp.insuranceCarrier || "",
      member_id: wp.memberId || "",
      plan_number: wp.planNumber || "",
      group_id: wp.groupNumber || "",
      certificate_number: wp.certificateNumber || "",
      plan_sponsor: wp.planSponsor || "",
    },
  };
}


function _normalizeProfile(profile: WizardProfileDict, verifiedDob: string | null): Record<string, any> {
  // The wizard uses camelCase keys; the Mac patient endpoint expects
  // snake_case. Mirror the mapping that's already in OnboardingWizard's
  // /api/patient/profile body, so the sync agent can replay verbatim.

  // Build the structured insurance sub-doc the new schema expects. Legacy
  // flat keys (insurance_provider, insurance_id, group_number,
  // policy_number) stay for backwards compat with anything still reading
  // them; new readers prefer profile.insurance.{...} (see PatientInsurance).
  const insurance: Record<string, any> = {};
  if (profile.insuranceCarrier) insurance.provider = profile.insuranceCarrier;
  if (profile.planNumber) insurance.plan_number = profile.planNumber;
  if (profile.memberId) insurance.member_id = profile.memberId;
  if (profile.groupNumber) insurance.group_id = profile.groupNumber;
  if (profile.certificateNumber) insurance.certificate_number = profile.certificateNumber;
  if (profile.planSponsor) insurance.plan_sponsor = profile.planSponsor;
  // Plan-member relationship the patient confirmed ("self" by default). This
  // feeds the form's "relationship to plan member" field via the insurance
  // sub-doc. We deliberately do NOT set the top-level plan_member_relationship
  // from this — that key drives the split-link two-signer dispatch, which
  // requires a plan_member_email the patient wizard doesn't collect (see
  // bridge.py use_split_link). Subscriber co-signing stays a physician-
  // initiated decision.
  if (profile.relationshipToPlanholder) insurance.relationship = profile.relationshipToPlanholder;

  return {
    first_name: profile.firstName,
    last_name: profile.lastName,
    date_of_birth: profile.dateOfBirth || verifiedDob,
    gender: profile.gender || null,
    health_care_number: profile.healthCardNumber,
    province: profile.province,
    phone: profile.phone,
    email: profile.email,
    address: [profile.streetAddress, profile.suite].filter(Boolean).join(", "),
    city: profile.city,
    postal_code: profile.postalCode,
    insurance: Object.keys(insurance).length > 0 ? insurance : null,
    insurance_provider: profile.insuranceCarrier,
    insurance_id: profile.memberId,
    group_number: profile.groupNumber,
    policy_number: profile.policyNumber || profile.planNumber,
    // Provincial drug coverage (Pharmacare etc.) — the existing
    // has_public_insurance boolean. null when the patient left it unanswered,
    // which only happens when it was never on file, so it never clobbers a
    // real value.
    has_public_insurance:
      typeof profile.hasPublicInsurance === "boolean" ? profile.hasPublicInsurance : null,
    // Secondary/supplemental private plan. Only the boolean (and provider) are
    // consumed by auto-fill — it drives the "other private plan" Yes/No on
    // forms. null when unanswered, which the engine treats as "no".
    has_supplemental_insurance:
      profile.hasSecondaryInsurance === "Yes"
        ? true
        : profile.hasSecondaryInsurance === "No"
        ? false
        : null,
    supplemental_insurance_provider: profile.secondaryInsuranceCarrier || null,
    // Plan member (when the patient isn't the primary planholder). Fills the
    // subscriber fields on group-insurance forms and drives the split-link
    // two-signer dispatch — the email gives the plan member their own link to
    // sign when the form needs their signature.
    plan_member_relationship: profile.relationshipToPlanholder || "self",
    plan_member_first_name: profile.planholderFirstName || null,
    plan_member_last_name: profile.planholderLastName || null,
    plan_member_dob: profile.planholderDob || null,
    plan_member_email: profile.planholderEmail || null,
    plan_member_phone: profile.planholderPhone || null,
    pharmacy_name: profile.pharmacyName,
    pharmacy_location: profile.pharmacyLocation || "",
    pharmacy_fax: profile.pharmacyFax,
    pharmacy_phone: profile.pharmacyPhone,
    physician_first_name: profile.physicianFirstName,
    physician_last_name: profile.physicianLastName,
    physician_phone: profile.physicianPhone,
    physician_fax: profile.physicianFax,
    physician_city: profile.physicianCity || null,
    authorization_signature:
      profile.signatureName ||
      `${profile.firstName || ""} ${profile.lastName || ""}`.trim(),
    signature_font: profile.signatureFont || null,
    // Onboarding completion marker. Set by handleWizardComplete after the
    // patient finishes the long form on first redemption. The Mac persists
    // this in patient_profiles.json; bridge preload then carries it back
    // on the next dispatch so BridgeOnboardingPage skips the WIZARD.
    onboarding_complete: profile.onboarding_complete || null,
    onboarding_completed_at: profile.onboarding_completed_at || null,
  };
}
